import decode from 'jwt-decode';
import { LOCAL_STORAGE, API_KEYS, HTTP } from './constants';
import {
    MILLISECONDS_TO_SECONDS_DIVISER,
    REFRESH_REMAINING_LIFE_RATIO,
    REFRESH_THRESHOLD_FLOOR_SECONDS,
    REFRESH_MIN_INTERVAL_SECONDS,
    REFRESH_TIMEOUT_MS,
    REFRESH_LOCK_WAIT_MS,
} from './constants/time';
const {
    LEXIKTOKEN,
    REFRESH_TOKEN,
    LAST_REFRESHED,
    UNAUTHENTICATED_LEXIKTOKEN,
    UNAUTHENTICATED_PROFESSIONAL_ID,
} = LOCAL_STORAGE;

const IS_AUTHENTICATED_2FA_IN_PROGRESS = 'IS_AUTHENTICATED_2FA_IN_PROGRESS'

/**
 * Name of the Web Locks lock used to elect a single refreshing tab. Held only for the duration of
 * one refresh, so contention is measured in hundreds of milliseconds.
 */
const REFRESH_LOCK_NAME = 'vf-access-token-refresh'

/**
 * The single in-flight refresh for this tab. Concurrent callers await this exact promise, so they
 * all observe the same resolution or the same rejection, and only one request leaves the tab.
 * Cleared in a `finally` so a failed refresh does not poison later attempts.
 */
let refreshPromise = null

/**
 * Bumped whenever the session is torn down (see `invalidateSession`). A refresh in flight across a
 * logout captures this value before its network call and refuses to persist its result if the value
 * has moved since, so a late-completing refresh cannot write tokens back over a cleared session.
 */
let sessionVersion = 0

/**
 * Aborts the refresh currently on the wire, if any. Without this the timeout only rejects the
 * waiters: the request itself keeps running, keeps the Web Lock held, and still lands its callback.
 */
let activeRefreshController = null

const abortActiveRefresh = () => {
    const controller = activeRefreshController

    activeRefreshController = null

    if (controller) {
        controller.abort()
    }
}

const nowInSeconds = () => Math.round(new Date().getTime() / MILLISECONDS_TO_SECONDS_DIVISER)

/**
 * Whether the session that spent `spentRefreshToken` is still the session in storage.
 *
 * `sessionVersion` is per-tab module state, so on its own it cannot see a logout in another tab, nor
 * the bare `localStorage.clear()` that the login and password-reset screens do, nor a re-login that
 * replaced the credentials. The stored refresh token is *shared* state and every one of those
 * teardowns removes or replaces it, so comparing against it covers all three.
 */
const sessionStillOwns = spentRefreshToken =>
    !!spentRefreshToken && localStorage.getItem(REFRESH_TOKEN) === spentRefreshToken

/**
 * Absolute expiry of a decoded token in seconds, or null when the token carries no usable claim.
 */
const getTokenExpiry = decodedToken => {
    const expiry = decodedToken?.exp || decodedToken?.expiry

    return typeof expiry === 'number' ? expiry : null
}

/**
 * Total lifetime of a decoded token in seconds, read from the token's own claims so the refresh
 * heuristic does not need to know what TTL the backend is currently issuing. Null when `iat` is
 * absent, in which case only the floor applies.
 */
const getTokenLifetime = decodedToken => {
    const expiry = getTokenExpiry(decodedToken)
    const issuedAt = decodedToken?.iat

    if (expiry === null || typeof issuedAt !== 'number' || expiry <= issuedAt) {
        return null
    }

    return expiry - issuedAt
}

const supportsWebLocks = () =>
    typeof navigator !== 'undefined' &&
    !!navigator.locks &&
    typeof navigator.locks.request === 'function' &&
    typeof AbortController === 'function'

/**
 * Rejects `promise`'s waiters if it has not settled within `timeoutMs`, so an unresolved refresh
 * cannot hang the admin behind a promise that never comes back.
 */
const withTimeout = (promise, timeoutMs, message) => {
    let timer

    return Promise.race([
        promise.finally(() => clearTimeout(timer)),
        new Promise((resolve, reject) => {
            timer = setTimeout(() => reject(new Error(message)), timeoutMs)
        }),
    ])
}

/**
 * Exchanges the stored refresh token for a new access token.
 *
 * The response is camelCase (`{ accessToken, refreshToken, expiresIn }`) and the endpoint is
 * unauthenticated - it takes no `vf-token`. Note that no error raised here contains token
 * material, so nothing sensitive can reach Sentry through a captured exception.
 */
const requestNewTokens = () => {
    const refreshToken = tokenManager.getRefreshToken()

    if (!refreshToken) {
        return Promise.reject(new Error('No refresh token available'))
    }

    // Captured before the network call so we can tell, on completion, whether the session was torn
    // down (logged out) while this refresh was in flight.
    const versionAtStart = sessionVersion
    const controller = typeof AbortController === 'function' ? new AbortController() : null

    activeRefreshController = controller

    return fetch(`${API_KEYS.api_url}/auth/refresh`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
        signal: controller ? controller.signal : undefined,
    })
        .then(response => {
            if (response.status === HTTP.UNAUTHORIZED) {
                // 401 is terminal: the refresh token is spent, revoked or unknown. There is
                // nothing left to authenticate with, so drop local state and let the caller
                // decide to log out. The status rides on the error so `authProvider.checkError`
                // treats it as an authentication failure rather than resolving and leaving the
                // authenticated UI mounted on a dead session.
                //
                // Only clear if this refresh still owns the session: an abandoned refresh that
                // 401s after the adviser logged back in must not wipe the new credentials.
                if (sessionStillOwns(refreshToken)) {
                    tokenManager.clearTokens()
                }

                const rejection = new Error('Token refresh was rejected')

                rejection.status = HTTP.UNAUTHORIZED

                throw rejection
            }

            if (!response.ok) {
                throw new Error('Token refresh failed')
            }

            return response.json()
        })
        .then(response => {
            const accessToken = response?.accessToken

            if (!accessToken) {
                throw new Error('Token refresh returned no access token')
            }

            if (sessionVersion !== versionAtStart || !sessionStillOwns(refreshToken)) {
                // The session this refresh belongs to ended while it was in flight — logged out in
                // this tab or another, cleared by the login/password-reset screens, or already
                // replaced by a fresh login. Persisting now would write the tokens back over that
                // teardown and strand a live credential, so drop the response instead.
                return null
            }

            // Written to localStorage *before* this promise settles. Resolving on the raw fetch
            // promise instead would let followers read the previous token straight back out.
            tokenManager.setTokens({
                token: accessToken,
                refreshToken: response?.refreshToken,
            })

            return accessToken
        })
        .finally(() => {
            // Released only once the whole exchange is done, not when the fetch promise settles:
            // that resolves at headers-received, and a refresh whose body is still streaming is
            // very much still in flight. Clearing it there would leave nothing to abort, so the
            // request would run on holding the Web Lock and starving the other tabs.
            if (activeRefreshController === controller) {
                activeRefreshController = null
            }
        })
}

/**
 * Elects a single refreshing tab with the Web Locks API. The lock holder is the leader and calls
 * the endpoint; everyone else queues behind it and, on acquiring the lock, finds a token they have
 * not seen before and uses that instead of spending the refresh token again - which would fail
 * once rotation is on.
 *
 * The wait is capped, so a tab that dies mid-refresh while holding the lock does not strand the
 * others. Where Web Locks is unavailable we simply refresh; see the PR notes for why that is an
 * acceptable trade-off for an internal admin tool.
 */
const refreshUnderLock = () => {
    if (!supportsWebLocks()) {
        return requestNewTokens()
    }

    const tokenBeforeWait = tokenManager.getLoggedInToken()
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REFRESH_LOCK_WAIT_MS)

    return navigator.locks
        .request(REFRESH_LOCK_NAME, { signal: controller.signal }, () => {
            const currentToken = tokenManager.getLoggedInToken()

            if (currentToken && currentToken !== tokenBeforeWait) {
                return currentToken
            }

            return requestNewTokens()
        })
        .finally(() => clearTimeout(timer))
}

const tokenManager = {
    getLoggedInToken: function () {
        return localStorage.getItem(LEXIKTOKEN)
    },

    getRefreshToken: function () {
        return localStorage.getItem(REFRESH_TOKEN)
    },

    isTwoFactor: function () {
        const token = this.decodeToken(this.getLoggedInToken())

        if (!this.verifyToken(token)) {
            return false
        }

        return (token['roles'] ?? []).includes(IS_AUTHENTICATED_2FA_IN_PROGRESS)
    },

    decodeToken: function (token) {
        try {
            if (token.startsWith('{')) {
                return JSON.parse(token)
            }

            return decode(token)
        } catch (e) {
            return null
        }
    },

    verifyToken: function (token) {
        if (!token || typeof token !== 'object') {
            return false
        }

        if (token?.roles === undefined) {
            return false
        }

        const expiry = token?.exp || token?.expiry;

        return expiry >= Math.round((new Date().getTime()) / MILLISECONDS_TO_SECONDS_DIVISER)
    },

    /**
     * True when the stored access token is past its expiry. False when there is no token or its
     * expiry cannot be read, so callers keep their existing behaviour for unparseable tokens.
     */
    hasExpiredAccessToken: function () {
        const expiry = getTokenExpiry(this.decodeToken(this.getLoggedInToken()))

        return expiry !== null && expiry < nowInSeconds()
    },

    /**
     * Whether to refresh ahead of a request: once roughly 80% of the access token's lifetime has
     * elapsed, subject to a floor on the threshold and a minimum interval between attempts. The
     * interval is what stops a client with a skewed clock refreshing on every single request.
     */
    shouldRefresh: function () {
        if (!this.getRefreshToken()) {
            return false
        }

        const decodedToken = this.decodeToken(this.getLoggedInToken())

        // Impersonated Professions do not refresh, so are auditable and locked to a TTL.
        if (decodedToken?.impersonatorId) {
            return false
        }

        const expiry = getTokenExpiry(decodedToken)

        if (expiry === null) {
            return false
        }

        const now = nowInSeconds()
        const lastRefreshed = Number(localStorage.getItem(LAST_REFRESHED))

        if (lastRefreshed && now - lastRefreshed < REFRESH_MIN_INTERVAL_SECONDS) {
            return false
        }

        const lifetime = getTokenLifetime(decodedToken)
        const threshold = Math.max(
            lifetime === null ? 0 : lifetime * REFRESH_REMAINING_LIFE_RATIO,
            REFRESH_THRESHOLD_FLOOR_SECONDS
        )

        return expiry - now <= threshold
    },

    setToken(token) {
        this.setTokens({ token })
    },

    /**
     * Persists a token pair. An absent `refreshToken` means "keep the one already stored" - both a
     * 2FA-interim login response and a pre-rotation refresh response omit it, and neither is an
     * error. It must never clear the stored token.
     */
    setTokens({ token, refreshToken }) {
        if (token) {
            localStorage.setItem(LEXIKTOKEN, token)
        }

        if (refreshToken) {
            localStorage.setItem(REFRESH_TOKEN, refreshToken)
        }

        localStorage.setItem(LAST_REFRESHED, String(nowInSeconds()))
        localStorage.removeItem(UNAUTHENTICATED_LEXIKTOKEN)
        localStorage.removeItem(UNAUTHENTICATED_PROFESSIONAL_ID)
    },

    /**
     * Drops every token-related key, including the unauthenticated ones that `setTokens()` clears.
     */
    clearTokens() {
        const keys = [
            LEXIKTOKEN,
            REFRESH_TOKEN,
            LAST_REFRESHED,
            UNAUTHENTICATED_LEXIKTOKEN,
            UNAUTHENTICATED_PROFESSIONAL_ID,
        ]

        keys.forEach(key => localStorage.removeItem(key))
    },

    /**
     * Marks the current session as torn down so a refresh already in flight cannot persist its
     * tokens after the fact. Called by logout; see `sessionVersion`.
     */
    invalidateSession() {
        sessionVersion += 1
        abortActiveRefresh()
    },

    /**
     * Refreshes the access token, at most once at a time per tab.
     *
     * @returns {Promise<string>} the new access token, already written to localStorage.
     */
    refreshAccessToken: function () {
        if (refreshPromise) {
            return refreshPromise
        }

        refreshPromise = withTimeout(refreshUnderLock(), REFRESH_TIMEOUT_MS, 'Token refresh timed out')
            .catch(error => {
                // The timeout only rejects the waiters. Cancel the request itself too, otherwise it
                // runs on with nobody listening, holding the Web Lock and blocking other tabs for
                // the remainder of its life.
                abortActiveRefresh()

                throw error
            })
            .finally(() => {
                refreshPromise = null
            })

        return refreshPromise
    },

    /**
     * Refreshes only when the access token is near expiry. Resolves with null when no refresh was
     * needed, so callers can treat it as a no-op gate before a request.
     */
    refreshIfNeeded: function () {
        if (!this.shouldRefresh()) {
            return Promise.resolve(null)
        }

        return this.refreshAccessToken()
    },
}

export default tokenManager
